import React, { useContext } from 'react'
import * as Styled from './BusinessHours.styles'
import PropTypes from 'prop-types'
import {
  Button,
  Grid,
  Icon,
  OpeningHours,
  OpeningIndicator,
} from 'components/atoms'
import { TextContext } from 'hocs'
import DOMPurify from 'dompurify'
import { entryPointId } from 'settings/app/constants'

/**
 * functional container for all detail views
 * @returns {*}
 * @constructor
 */
const BusinessHours = ({
  data,
  openingIndicatorData,
  appointmentUrl,
  entryPoint,
}) => {
  const {
    openingHours,
    consultationHours,
    specialHours,
    infos,
    aokInfo,
    openingHoursHints,
  } = data
  const hoursData = []

  if (openingHours) hoursData.push(openingHours)
  if (consultationHours) hoursData.push(consultationHours)
  if (specialHours) hoursData.push(specialHours)

  // show time notice only once and hide for day and night care
  const timeNoticeIndex =
    (openingHours && consultationHours) ||
    entryPoint === entryPointId.DAYANDNIGHTCARE
      ? 1
      : 0
  const textContext = useContext(TextContext)
  let filteredInfoList = {}

  if (infos?.list) {
    filteredInfoList = Object.keys(infos.list)
      .filter((key) => null !== infos.list[key])
      .reduce((obj, key) => {
        obj[key] = infos.list[key]

        return obj
      }, {})
  }

  const handleAppointmentClick = () => {
    window.open(appointmentUrl, '_blank')
  }

  return (
    <Styled.BusinessHoursComponent>
      <Grid container>
        <Grid item xs={ 12 } md={ 4 }>
          {openingIndicatorData && (
            <OpeningIndicator
              type={ openingIndicatorData.state }
              untilMsg={ openingIndicatorData.message }
              className={ 'general-info-opening' }
            />
          )}
        </Grid>
        <Grid item xs={ 12 } md={ 8 }>
          {hoursData.map((item, index) => {
            const { headline, copy, info, hours } = item
            return (
              <div key={ index }>
                {headline && (
                  <p className={ 'business-headline-mb' }>
                    <b>{headline}</b>
                  </p>
                )}
                {copy && <p className={ 'business-copy' }>{copy}</p>}
                {info && <p className={ 'business-info' }>{info}</p>}
                {hours && (
                  <OpeningHours
                    openingHours={ hours }
                    showTimeNotice={ timeNoticeIndex === index }
                    className={ `business-hours ${
                      timeNoticeIndex === index ? 'has-time-notice' : ''
                    }` }
                  />
                )}
              </div>
            )
          })}
          {openingHoursHints &&
            openingHoursHints.length > 0 &&
            openingHoursHints.copy && (
              <div className='opening-hours-notice-list'>
                {openingHoursHints.map((hint, index) => (
                  <div key={ index }>
                    <p className={ 'business-headline-mb' }>
                      <b>{hint.headline}</b>
                    </p>
                    <p
                      className={ 'aok-info-copy' }
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(hint.copy),
                      }}
                    />
                  </div>
                ))}
              </div>
            )}

          {Object.keys(filteredInfoList).length > 0 && (
            <div className={ 'business-info-list' }>
              {infos.headline && (
                <p>
                  <b>{infos.headline}</b>
                </p>
              )}
              <ul className={ 'list-checkmark' }>
                {Object.keys(filteredInfoList).map((key, index) => (
                  <li
                    key={ index }
                    className={ filteredInfoList[key] ? '' : 'not-available' }>
                    {key}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {aokInfo && aokInfo?.copy && (
            <>
              {aokInfo.headline && (
                <p className={ 'business-headline-mb' }>
                  <b>{aokInfo.headline}</b>
                </p>
              )}
              {aokInfo.copy && (
                <p
                  className={ 'aok-info-copy' }
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(aokInfo.copy, {
                      ALLOWED_ATTR: ['href', 'rel', 'target', 'title'],
                    }),
                  }}
                />
              )}
            </>
          )}
          {appointmentUrl && (
            <Button
              id={ 'border-btn' }
              text={ textContext['general.makeAnAppointment'] }
              onClick={ handleAppointmentClick }
              className={ 'with-border btn-appointment' }
              textFirst={ true }
              icon={ <Icon className='icon-f-new-tab' /> }
            />
          )}
        </Grid>
      </Grid>
    </Styled.BusinessHoursComponent>
  )
}

const hoursData = PropTypes.shape({
  headline: PropTypes.string,
  copy: PropTypes.string,
  info: PropTypes.string,
  hours: PropTypes.object,
})

BusinessHours.propTypes = {
  data: PropTypes.shape({
    openingHours: hoursData,
    consultationHours: hoursData,
    specialHours: hoursData,
    infos: PropTypes.shape({
      headline: PropTypes.string,
      list: PropTypes.object,
    }),
    aokInfo: PropTypes.shape({
      headline: PropTypes.string,
      copy: PropTypes.string,
    }),
    openingHoursHints: PropTypes.arrayOf(
      PropTypes.shape({
        headline: PropTypes.string,
        copy: PropTypes.string,
      })
    ),
  }),
  openingIndicatorData: PropTypes.shape({
    state: PropTypes.string,
    message: PropTypes.string,
  }),
  appointmentUrl: PropTypes.string,
  entryPoint: PropTypes.oneOf([
    entryPointId.DOCTORS,
    entryPointId.DAYANDNIGHTCARE,
  ]).isRequired,
}

export default BusinessHours
